import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { getSliders, getImageUrl } from '../../services/sliderService';
import { Box, Typography, Button, Container } from '@mui/material';
import Slider from 'react-slick';
import { Link as RouterLink } from 'react-router-dom';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

const SliderInstituicao = ({ instituicaoId, instituicaoNome, modo = 'privado' }) => {
  const [sliders, setSliders] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [imageErrors, setImageErrors] = useState({});

  useEffect(() => {
    const fetchSliders = async () => {
      setLoading(true);
      try {
        console.log('Buscando sliders para instituição:', instituicaoId);
        const data = await getSliders(instituicaoId, modo);
        console.log('Dados dos sliders recebidos:', data);
        
        // Log detalhado das URLs das imagens
        data.forEach(slider => {
          const imageUrl = getImageUrl(slider.imagemUrl);
          console.log(`Slider ${slider.id}:`, {
            original: slider.imagemUrl,
            processed: imageUrl
          });
        });

        setSliders(data);
      } catch (error) {
        console.error('Erro ao buscar sliders:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    if (instituicaoId) {
      fetchSliders();
    }
  }, [instituicaoId, modo]);

  const handleImageError = (sliderId) => {
    console.error(`Erro ao carregar imagem do slider ${sliderId}`);
    setImageErrors(prev => ({
      ...prev,
      [sliderId]: true
    }));
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography>Carregando sliders...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  if (!sliders || sliders.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography>Nenhum slider disponível.</Typography>
      </Box>
    );
  }

  const settings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 5000,
    adaptiveHeight: true,
  };

  return (
    <Box sx={{ width: '100%', overflow: 'hidden' }}>
      <Slider {...settings}>
        {sliders.map((slider) => (
          <Box
            key={slider.id}
            sx={{
              width: '100%',
              height: { xs: '40vh', sm: '50vh', md: '60vh' },
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <img
              src={getImageUrl(slider.imagemUrl)}
              alt={slider.titulo || 'Slider image'}
              onError={() => handleImageError(slider.id)}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                objectPosition: 'center',
              }}
            />
            {/* Overlay escuro */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0,0,0,0.5)',
                zIndex: 1,
              }}
            />
            
            {/* Conteúdo do slider */}
            <Container 
              maxWidth="md" 
              sx={{ 
                position: 'relative', 
                zIndex: 2, 
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Typography
                variant="h3"
                component="h1"
                gutterBottom
                sx={{ 
                  color: 'white', 
                  textAlign: 'center',
                  fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3rem' },
                  mb: 2
                }}
              >
                {slider.titulo || (modo === 'privado' ? `Bem-vindo, ${instituicaoNome}!` : instituicaoNome)}
              </Typography>
              
              <Typography
                variant="h6"
                sx={{ 
                  mb: 4, 
                  color: 'white', 
                  textAlign: 'center',
                  fontSize: { xs: '1rem', sm: '1.25rem' },
                }}
              >
                {slider.descricao || (modo === 'privado' ? 'Gerencie suas campanhas e pré-inscrições com facilidade.' : '')}
              </Typography>

              {slider.linkUrl && (
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    component={RouterLink}
                    to={slider.linkUrl}
                    sx={{
                      mt: 2,
                      px: 4,
                      py: 1.5,
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                    }}
                  >
                    {slider.linkTexto || 'Saiba Mais'}
                  </Button>
                </Box>
              )}
            </Container>
          </Box>
        ))}
      </Slider>
    </Box>
  );
};

SliderInstituicao.propTypes = {
  instituicaoId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  instituicaoNome: PropTypes.string.isRequired,
  modo: PropTypes.oneOf(['publico', 'privado'])
};

SliderInstituicao.defaultProps = {
  modo: 'privado'
};

export default SliderInstituicao;